// ============================================
// Cloudflare Turnstile Component
// ============================================
// React wrapper for Cloudflare Turnstile widget (Managed mode)
// Reference: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/

import { useEffect, useRef, useCallback, useState } from 'react';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Declare global Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: (error?: string) => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
  'refresh-expired'?: 'auto' | 'manual' | 'never';
}

export interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error?: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  className?: string;
}

// Script loading state (shared across instances)
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;
  if (window.turnstile) return Promise.resolve();

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${TURNSTILE_SCRIPT_URL}?onload=onTurnstileLoad&render=explicit`;
    script.async = true;
    script.defer = true;

    window.onTurnstileLoad = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Failed to load Turnstile script'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function Turnstile({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
  className,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleVerify = useCallback((token: string) => onVerify(token), [onVerify]);
  const handleError = useCallback((error?: string) => onError?.(error), [onError]);
  const handleExpire = useCallback(() => onExpire?.(), [onExpire]);

  useEffect(() => {
    let mounted = true;

    const initTurnstile = async () => {
      try {
        await loadTurnstileScript();

        if (!mounted || !containerRef.current || !window.turnstile) return;

        // Remove existing widget if any
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // Ignore removal errors
          }
        }

        // Render widget
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: handleVerify,
          'error-callback': handleError,
          'expired-callback': handleExpire,
          theme,
          size,
          retry: 'auto',
          'retry-interval': 5000,
          'refresh-expired': 'auto',
        });

        widgetIdRef.current = widgetId;
        setIsLoading(false);
      } catch {
        if (mounted) {
          setLoadError('Failed to load verification widget');
          setIsLoading(false);
        }
      }
    };

    initTurnstile();

    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore removal errors
        }
      }
    };
  }, [siteKey, handleVerify, handleError, handleExpire, theme, size]);

  if (loadError) {
    return (
      <div className="text-sm text-destructive p-3 bg-destructive/10 rounded-lg">
        {loadError}. Please refresh the page.
      </div>
    );
  }

  return (
    <div className={className}>
      {isLoading && (
        <div className="h-[65px] flex items-center justify-center text-muted-foreground text-sm">
          Loading verification...
        </div>
      )}
      <div
        ref={containerRef}
        className={isLoading ? 'hidden' : ''}
        data-testid="turnstile-widget"
      />
    </div>
  );
}
